import React, { useEffect, useState } from 'react'
import { Table, TableCell, TableHead, TableRow, TableBody, Typography } from '@material-ui/core'
import * as lodash from 'lodash'
import { ChangeMergeFunction, Indicator, MergeProperties, RepositoryMergeProperties } from '../../../../types'
import { ErrorIndicator } from './ErrorIndicator'
import { CheckboxComponent } from './CheckBoxComponent'
import SourceBranchIcon from '@material-ui/icons/CallSplit'
import DeleteIcon from '@material-ui/icons/Cancel'
import DestinationIcon from '@material-ui/icons/CallMerge'
import RepositoryIcon from '@material-ui/icons/LibraryBooks'
import AddIcon from '@material-ui/icons/LibraryAddCheck'
import { TableHeadCell } from '../../../common/TableHeadCell'
import { SelectComponent } from '../../../common/Select'
import { Arrow } from '../../../common/Arrow'

export function MergeTableComponent(props: {
  repositories: RepositoryMergeProperties[]
  mergeInformation: MergeProperties[]
  onMergeChange: ChangeMergeFunction
  errorIndicator: Indicator<string>[]
}) {
  const { repositories, mergeInformation, onMergeChange, errorIndicator } = props
  const [closeIndicator, setCloseIndicator] = useState([] as Indicator<boolean>[])
  const [includeIndicator, setIncludeIndicator] = useState([] as Indicator<boolean>[])
  const [display, setDisplay] = useState(false)

  useEffect(() => {
    if (
      repositories.length > 0 &&
      mergeInformation.length > 0 &&
      errorIndicator.length > 0 &&
      includeIndicator.length > 0 &&
      closeIndicator.length > 0
    ) {
      setDisplay(true)
    }
  }, [repositories, mergeInformation, errorIndicator, closeIndicator, includeIndicator])

  useEffect(() => {
    setIncludeIndicator(
      mergeInformation.map(mergeInfo => {
        return {
          fetching: false,
          value: !mergeInfo.diff
        }
      })
    )
  }, [mergeInformation])

  useEffect(() => {
    const newCloseIndicator = mergeInformation.map((mergeInfo, index) => {
      const disabled =
        repositories[index].productionBranch === mergeInfo.source ||
        repositories[index].developmentBranch === mergeInfo.source
      return {
        fetching: false,
        value: disabled
      }
    })
    setCloseIndicator(newCloseIndicator)
  }, [mergeInformation, repositories])

  function setFetchingIndicators(index: number) {
    const newCloseIndicator = lodash.cloneDeep(closeIndicator)
    newCloseIndicator[index].fetching = true
    const newIncludeIndicator = lodash.cloneDeep(includeIndicator)
    newIncludeIndicator[index].fetching = true
    setCloseIndicator(newCloseIndicator)
    setIncludeIndicator(newIncludeIndicator)
  }

  function changeSource(value: string, index: number) {
    setFetchingIndicators(index)
    onMergeChange('CHANGE_SOURCE', index, value)
  }
  function changeDestination(value: string, index: number) {
    setFetchingIndicators(index)
    onMergeChange('CHANGE_DESTINATION', index, value)
  }
  function changeClose(value: boolean, index: number) {
    onMergeChange('CHANGE_CLOSE', index, value)
  }
  function changeInclude(value: boolean, index: number) {
    onMergeChange('CHANGE_INCLUDE', index, value)
  }

  function renderRepositories() {
    if (display) {
      return repositories.map((repository, index) => {
        const applyHighlight = mergeInformation[index].diff ? 'highlight' : ''
        return (
          <TableRow className={applyHighlight} key={index}>
            <TableCell>
              <Typography variant='body1'>{repository.name}</Typography>
            </TableCell>
            <TableCell>
              <SelectComponent
                callback={changeSource}
                index={index}
                value={mergeInformation[index].source}
                items={repository.branches}
              />
            </TableCell>
            <TableCell>
              <Arrow display={true} tip={'from => to'} />
            </TableCell>
            <TableCell>
              <SelectComponent
                callback={changeDestination}
                index={index}
                value={mergeInformation[index].destination}
                items={repository.branches}
              />
            </TableCell>
            <TableCell>
              <CheckboxComponent
                fetching={closeIndicator[index].fetching}
                disabled={closeIndicator[index].value}
                checked={mergeInformation[index].close}
                onChange={(checked: boolean) => {
                  changeClose(checked, index)
                }}
              />
            </TableCell>
            <TableCell>
              <CheckboxComponent
                fetching={includeIndicator[index].fetching}
                disabled={includeIndicator[index].value}
                checked={mergeInformation[index].include}
                onChange={(checked: boolean) => {
                  changeInclude(checked, index)
                }}
              />
            </TableCell>
            <TableCell>
              <ErrorIndicator indicator={errorIndicator[index]} />
            </TableCell>
          </TableRow>
        )
      })
    }
    return null
  }

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeadCell width={20} text={'Repository'} icon={<RepositoryIcon />} />
          <TableHeadCell width={15} text={'Source'} icon={<SourceBranchIcon />} />
          <TableHeadCell width={5} />
          <TableHeadCell width={15} text={'Destination'} icon={<DestinationIcon />} />
          <TableHeadCell width={10} text={'Close'} icon={<DeleteIcon />} />
          <TableHeadCell width={10} text={'Merge'} icon={<AddIcon />} />
          <TableHeadCell width={100} />
        </TableRow>
      </TableHead>
      <TableBody>{renderRepositories()}</TableBody>
    </Table>
  )
}
